// client/src/utils/likeApi.js
import auth from "../config/firebase.js";
import axios from "axios";

// Cache token with expiry to reduce unnecessary auth calls
let cachedToken = null;
let tokenExpiry = 0;
const TOKEN_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

async function getAuthToken() {
  const now = Date.now();
  if (cachedToken && now < tokenExpiry) {
    return cachedToken;
  }

  const user = auth.currentUser;
  if (!user) return null;

  try {
    const token = await user.getIdToken();
    cachedToken = token;
    tokenExpiry = now + TOKEN_CACHE_DURATION;
    return token;
  } catch (error) {
    console.error("Error fetching auth token:", error);
    return null;
  }
}

const API_BASE_URL = "/api/likes";

// Helper for API requests with consistent error handling using Axios
async function apiRequest(url, method, data = null) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error("Authentication required");
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    url: url,
    headers: headers,
  };

  // Conditionally add 'data' property for methods that typically send a request body
  if (method === "POST" || method === "PUT" || method === "PATCH") {
    config.data = data;
  }

  try {
    const response = await axios(config); // Use axios directly with config object

    // Axios automatically parses JSON, so response.data is already the parsed object
    const responseData = response.data;

    // Axios throws an error for non-2xx status codes, so we don't need response.ok check here
    // However, we can still check for a 'success' property if your backend always returns it
    if (responseData.success === false) {
      // Assuming your backend sends { success: false, message: "..." } on logical errors
      throw new Error(
        responseData.message || `Request failed with status ${response.status}`
      );
    }

    return responseData;
  } catch (error) {
    // Axios error handling:
    // error.response: The response received (if any)
    // error.request: The request that was made (if no response was received)
    // error.message: Error message
    // error.config: The config that was used for the request

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.status === 401) {
        cachedToken = null; // Clear token cache on 401
        throw new Error("Authentication expired or invalid token");
      }
      throw new Error(
        error.response.data.message ||
          `Request failed with status ${error.response.status}`
      );
    } else if (error.request) {
      // The request was made but no response was received
      console.error("Network error - no response received:", error.request);
      throw new Error("Network error - please check your connection");
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error("Error setting up request:", error.message);
      throw new Error(`Request setup error: ${error.message}`);
    }
  }
}

export async function likeItem(userId, itemId) {
  // Axios uses 'data' for the body of POST requests
  const response = await apiRequest(API_BASE_URL, "POST", { userId, itemId });
  return response.success === true;
}

export async function unlikeItem(userId, itemId) {
  // For DELETE requests, data is passed as the third argument to apiRequest, but will now be ignored for the body
  const response = await apiRequest(
    `${API_BASE_URL}/user/${userId}/item/${itemId}`,
    "DELETE"
  );
  return response.success;
}

export async function checkItemLiked(userId, itemId) {
  try {
    // For GET requests, 'data' parameter is usually null or undefined, and will now be ignored for the body
    const response = await apiRequest(
      `${API_BASE_URL}/user/${userId}/item/${itemId}`,
      "GET"
    );

    console.log(
      `[likeApi] checkItemLiked for user ${userId}, item ${itemId}. Raw API response:`,
      response
    );
    console.log(
      `[likeApi] checkItemLiked result (response.liked):`,
      response.liked
    );

    return response.liked === true;
  } catch (error) {
    console.error(
      `[likeApi Error] in checkItemLiked for user ${userId}, item ${itemId}:`,
      error
    );
    return false;
  }
}

// New function to get the like count for a single item
export async function getItemLikeCount(itemId) {
  try {
    // Your backend endpoint is set up for POST with a body of { itemIds: [] }
    const response = await apiRequest(`${API_BASE_URL}/counts`, "POST", {
      itemIds: [itemId],
    });

    if (response.success && response.data) {
      // The response.data will be an object like { "itemId1": count1, "itemId2": count2 }
      return response.data[itemId] || 0; // Return the count for the specific itemId, or 0 if not found
    }
    return 0; // Default to 0 if API response is not as expected
  } catch (error) {
    console.error(
      `[likeApi Error] in getItemLikeCount for item ${itemId}:`,
      error
    );
    return 0; // Return 0 on error
  }
}
